/**
 * The document title, built from the trail the breadcrumb already resolved.
 *
 * A tab strip shows the first twenty characters or so, so the title reads most
 * specific first: the page, then the one thing it belongs to, then the product.
 * The middle of a long trail is dropped rather than compressed, because a
 * truncated chain of ancestors names nothing.
 *
 * @module components/shell/page-title
 */

/** Always the last word, so a tab is recognisable once the rest is cut. */
export const PRODUCT = "SocratIQ";

/** How long a context may run before it is cut. */
export const CONTEXT_MAX = 40;

/** Between the parts. Never an em dash or a pipe. */
const SEPARATOR = " · ";

/**
 * @param text - The context, which is often a programme title.
 * @param max - Where to cut.
 * @returns It, cut on a word boundary when it is longer than `max`.
 */
export function truncateContext(text: string, max: number = CONTEXT_MAX): string {
  if (text.length <= max) {
    return text;
  }
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  // A single word longer than the limit has no boundary to cut on, so it is
  // cut where it runs out rather than dropped entirely.
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

export interface PageTitleOptions {
  /** Prefixed, because a tab is where an operator sees whose session this is. */
  impersonating?: boolean;
}

/**
 * @param labels - The trail, outermost first, as the breadcrumb reads it.
 * @param options - Whether this session is impersonating someone.
 * @returns The document title. The product alone when the trail is empty.
 *
 * @example
 * ```ts
 * composePageTitle(["Programmes", "B.Sc. in Cognitive Computation", "Identity"]);
 * // "Identity · B.Sc. in Cognitive Computation · SocratIQ"
 * ```
 */
export function composePageTitle(
  labels: readonly string[],
  { impersonating = false }: PageTitleOptions = {},
): string {
  const trail = labels.filter((label) => label.trim());
  const page = trail.at(-1);
  // The outermost crumb names the workspace itself, which the product name
  // already says, so it is never the context: a top-level screen reads
  // "Portfolio · SocratIQ" rather than repeating where every screen lives.
  const context = trail.length > 2 ? trail.at(-2) : undefined;
  const parts = [page, context === undefined ? undefined : truncateContext(context), PRODUCT];
  return `${impersonating ? "[Impersonating] " : ""}${parts.filter(Boolean).join(SEPARATOR)}`;
}
